(() => {
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-hero-media]').forEach((container) => {
      if (container.dataset.initialized) return;
      container.dataset.initialized = 'true';
      const video = container.querySelector('video');
      const button = container.querySelector('[data-motion-toggle]');
      if (!video || !button) return;
      button.hidden = false;
      video.muted = true;
      video.setAttribute('playsinline', '');
      let pausedByUser = false;
      let visible = false;
      const update = () => {
        button.setAttribute('aria-label', video.paused ? button.dataset.play : button.dataset.pause);
        button.firstElementChild.textContent = video.paused ? '▶' : 'Ⅱ';
        if (!video.paused) container.classList.add('has-playing-video');
      };
      const play = () => {
        if (video.dataset.src && !video.hasAttribute('src')) {
          video.src = window.matchMedia('(max-width: 800px)').matches && video.dataset.mobileSrc ? video.dataset.mobileSrc : video.dataset.src;
        }
        video.play().then(update).catch(update);
      };
      const autoplay = () => {
        if (visible && !document.hidden && !motionPreference.matches && !pausedByUser && container.isConnected) play();
      };
      video.addEventListener('playing', update);
      video.addEventListener('pause', update);
      video.addEventListener('error', () => { container.classList.remove('has-playing-video'); button.hidden = true; });
      button.addEventListener('click', () => {
        if (video.paused) { pausedByUser = false; play(); }
        else { pausedByUser = true; video.pause(); }
      });
      if (typeof IntersectionObserver === 'function') {
        const observer = new IntersectionObserver((entries) => {
          visible = entries[0].isIntersecting;
          if (!visible) video.pause();
          else autoplay();
        }, { threshold: .15 });
        observer.observe(container);
      }
      const onMotionChange = () => {
        if (motionPreference.matches) video.pause();
        else autoplay();
      };
      if (typeof motionPreference.addEventListener === 'function') motionPreference.addEventListener('change', onMotionChange);
      else if (typeof motionPreference.addListener === 'function') motionPreference.addListener(onMotionChange);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) video.pause();
        else autoplay();
      });
      update();
    });
  };
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-nav-toggle]');
    if (toggle) {
      const expanded = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(expanded));
      document.getElementById(toggle.getAttribute('aria-controls')).classList.toggle('is-open', expanded);
      document.body.classList.toggle('menu-open', expanded);
    }
    if (event.target.closest('.primary-nav a')) {
      document.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', 'false');
      document.querySelector('.primary-nav')?.classList.remove('is-open');
      document.body.classList.remove('menu-open');
    }
    const thumbnail = event.target.closest('[data-gallery-target]');
    if (thumbnail) {
      const gallery = thumbnail.closest('.product-gallery');
      gallery.querySelectorAll('video').forEach((video) => video.pause());
      const panel = document.getElementById(thumbnail.dataset.galleryTarget);
      if (panel) gallery.querySelector('.product-main-media').innerHTML = panel.innerHTML;
      gallery.querySelectorAll('[data-gallery-target]').forEach((button) => button.setAttribute('aria-pressed', String(button === thumbnail)));
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const toggle = document.querySelector('[data-nav-toggle][aria-expanded="true"]');
    if (toggle) { toggle.click(); toggle.focus(); }
    document.querySelectorAll('.nav-dropdown[open]').forEach((details) => { details.open = false; details.querySelector('summary').focus(); });
  });
  let selectionController;
  document.addEventListener('change', async (event) => {
    const selector = event.target.closest('[data-option-select]');
    if (!selector) return;
    const section = selector.closest('[data-product-section]');
    const selections = [...section.querySelectorAll('[data-option-select]')].map((select) => select.value);
    const addButton = section.querySelector('[data-add-button]');
    const variantInput = section.querySelector('[data-variant-input]');
    addButton.disabled = true;
    variantInput.disabled = true;
    selectionController?.abort();
    if (selections.some((selection) => !selection)) return;
    selectionController = new AbortController();
    const focusId = selector.id;
    const quantity = section.querySelector('[name="quantity"]').value;
    const chosen = selector.options[selector.selectedIndex];
    const url = new URL(chosen.dataset.productUrl || section.dataset.productUrl, window.location.origin);
    url.searchParams.set('option_values', selections.join(','));
    url.searchParams.set('section_id', section.dataset.sectionId);
    section.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: selectionController.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Selection request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('[data-product-section]');
      if (!replacement) throw new Error('Product section missing');
      replacement.querySelector('[name="quantity"]').value = quantity;
      replacement.querySelectorAll('[data-option-select]').forEach((select, index) => { select.value = selections[index]; });
      if ([...replacement.querySelectorAll('[data-option-select]')].some(select => !select.value)) {
        replacement.querySelector('[data-variant-input]').disabled = true;
        replacement.querySelector('[data-add-button]').disabled = true;
        replacement.querySelector('[data-product-status]').textContent = replacement.dataset.selectionHint;
      }
      if (!replacement.querySelector('[data-variant-input]').value) {
        replacement.querySelector('[data-product-status]').textContent = replacement.dataset.unavailable;
        replacement.querySelector('[data-add-button]').textContent = replacement.dataset.unavailable;
      }
      section.replaceWith(replacement);
      document.getElementById(focusId)?.focus({ preventScroll: true });
      const variant = replacement.querySelector('[data-variant-input]').value;
      const canonical = new URL(url);
      canonical.searchParams.delete('section_id');
      if (variant) { canonical.searchParams.delete('option_values'); canonical.searchParams.set('variant', variant); }
      window.history.replaceState({}, '', canonical.pathname + canonical.search);
      document.title = html.querySelector('h1')?.textContent.trim() ? `${html.querySelector('h1').textContent.trim()} — ${document.title.split(' — ').pop()}` : document.title;
    } catch (error) {
      if (error.name === 'AbortError') return;
      section.querySelector('[data-product-status]').textContent = section.dataset.loadError;
    } finally {
      if (section.isConnected) section.removeAttribute('aria-busy');
    }
  });
  const updateProvinces = (country, useSaved = false) => {
    const province = document.getElementById(country.dataset.provinceId);
    if (!province) return;
    const options = JSON.parse(country.options[country.selectedIndex]?.dataset.provinces || '[]');
    province.replaceChildren(...options.map(([value, label]) => {
      const option = new Option(label, value);
      option.selected = useSaved && (value === province.dataset.selectedProvince || label === province.dataset.selectedProvince);
      return option;
    }));
    province.closest('[data-province-field]').hidden = options.length === 0;
    province.disabled = options.length === 0;
  };
  document.querySelectorAll('[data-address-country]').forEach((country) => {
    const match = [...country.options].find(option => option.value === country.dataset.selectedCountry || option.text === country.dataset.selectedCountry);
    if (match) country.value = match.value;
    updateProvinces(country, true);
    country.addEventListener('change', () => updateProvinces(country));
  });
  const initializeHero = (scope = document) => {
    try { initialize(scope); }
    catch (error) { console.error('Waterbury: hero video initialization failed.', error); }
  };
  initializeHero();
  document.addEventListener('shopify:section:load', (event) => initializeHero(event.target));
})();

(() => {
  const initialized = new WeakSet();
  const hoverPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-product-card]').forEach((card) => {
      if (initialized.has(card)) return;
      const media = card.querySelector('.product-card__media');
      const visual = card.querySelector('.product-card__visual') || media;
      const front = card.querySelector('.product-card__image--front');
      const back = card.querySelector('.product-card__image--back');
      const button = card.querySelector('[data-product-view-toggle]');
      const status = card.querySelector('[data-product-view-status]');
      if (!media || !front || !back?.dataset.hoverSrc || !button) return;
      initialized.add(card);
      const label = button.querySelector('[data-product-view-label]') || button;
      let choice = null;
      let hovered = false;
      let focused = false;
      let ready = false;
      let failed = false;
      let loading;
      const wantsBack = () => choice === null ? hovered || focused : choice === 'back';
      const render = () => {
        const requested = wantsBack() && !failed;
        const showing = ready && requested;
        card.classList.toggle('is-showing-back', showing);
        front.setAttribute('aria-hidden', String(showing));
        back.setAttribute('aria-hidden', String(!showing));
        button.setAttribute('aria-pressed', String(requested));
        button.setAttribute('aria-busy', String(requested && !ready));
        label.textContent = requested ? button.dataset.showFront : button.dataset.showBack;
      };
      const loadBack = () => {
        if (loading) return loading;
        loading = new Promise((resolve, reject) => {
          let settled = false;
          const cleanup = () => {
            back.removeEventListener('load', loaded);
            back.removeEventListener('error', fail);
          };
          const fail = () => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error('Back image unavailable'));
          };
          const loaded = async () => {
            if (settled) return;
            settled = true;
            cleanup();
            try {
              if (back.decode) {
                try { await back.decode(); }
                catch (error) { if (!back.naturalWidth) throw error; }
              }
              if (!back.naturalWidth) throw new Error('Back image unavailable');
              back.hidden = false;
              window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
                ready = true;
                resolve();
              }));
            } catch (error) {
              reject(error);
            }
          };
          back.addEventListener('load', loaded);
          back.addEventListener('error', fail);
          back.loading = 'eager';
          back.decoding = 'async';
          if (back.dataset.hoverSizes) back.sizes = back.dataset.hoverSizes;
          if (back.dataset.hoverSrcset) back.srcset = back.dataset.hoverSrcset;
          back.src = back.dataset.hoverSrc;
          if (back.complete && back.naturalWidth) loaded();
        });
        return loading;
      };
      const update = () => {
        render();
        if (!wantsBack() || ready || failed) return;
        loadBack().then(render).catch(() => {
          failed = true;
          choice = 'front';
          back.hidden = true;
          back.removeAttribute('src');
          back.removeAttribute('srcset');
          if (status) {
            status.textContent = button.dataset.viewError;
            status.hidden = false;
          }
          render();
        });
      };
      visual.addEventListener('pointerover', (event) => {
        if (!hoverPointer.matches || event.pointerType === 'touch' || button.contains(event.target) || hovered) return;
        hovered = true;
        update();
      });
      visual.addEventListener('pointerleave', () => {
        hovered = false;
        if (choice === 'front') choice = null;
        update();
      });
      media.addEventListener('focus', () => {
        focused = media.matches(':focus-visible');
        update();
      });
      media.addEventListener('blur', () => {
        focused = false;
        if (choice === 'front' && !hovered) choice = null;
        update();
      });
      button.addEventListener('click', () => {
        choice = !failed && wantsBack() ? 'front' : 'back';
        if (failed && choice === 'back') {
          failed = false;
          loading = null;
          if (status) { status.hidden = true; status.textContent = ''; }
        }
        update();
      });
      button.hidden = false;
      render();
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

(() => {
  const initialized = new WeakSet();
  const initialize = (scope = document) => {
    const sections = [...scope.querySelectorAll('[data-category-filter-scope]')];
    if (scope.matches?.('[data-category-filter-scope]')) sections.unshift(scope);
    sections.forEach((section) => {
      if (initialized.has(section)) return;
      const filters = section.querySelector('[data-category-filter]');
      const cards = [...section.querySelectorAll('[data-product-card]')].filter((card) => card.closest('[data-category-filter-scope]') === section);
      if (!filters || !cards.length || cards.some((card) => !card.dataset.productCategory)) return;
      const buttons = [...filters.querySelectorAll('[data-category-value]')];
      const status = filters.querySelector('[data-category-status]');
      const counts = [...section.querySelectorAll('[data-category-count]')];
      const categories = new Set(cards.map((card) => card.dataset.productCategory));
      buttons.forEach((button) => { button.hidden = button.dataset.categoryValue !== 'all' && !categories.has(button.dataset.categoryValue); });
      const select = (category, announce = true) => {
        let visible = 0;
        cards.forEach((card) => {
          const shown = category === 'all' || card.dataset.productCategory === category;
          card.hidden = !shown;
          if (shown) visible += 1;
        });
        buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.categoryValue === category)));
        const template = visible === 1 ? filters.dataset.countOne : filters.dataset.countOther;
        const message = template.split('{count}').join(String(visible));
        counts.forEach((count) => { count.textContent = message; });
        if (announce && status) status.textContent = message;
      };
      filters.addEventListener('click', (event) => {
        const button = event.target.closest('[data-category-value]');
        if (!button || !filters.contains(button) || button.hidden) return;
        select(button.dataset.categoryValue);
      });
      initialized.add(section);
      select('all', false);
      filters.hidden = false;
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
